
import { useState, useEffect } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [highlightService, setHighlightService] = useState(false);

  const services = [
    'Photovoltaik (PV-Anlage)',
    'Alarmanlagen & Sicherheitstechnik',
    'Blitzschutz',
    'Elektroinstallationen',
    'Beleuchtung & Beschallung',
    'Notdienst',
    'Wartung',
    'Beratung',
    'Sonstiges',
  ];

  useEffect(() => {
    const handlePreselect = (e: Event) => {
      const customEvent = e as CustomEvent<string>;
      if (customEvent.detail) {
        setFormData((prev) => ({ ...prev, service: customEvent.detail }));
        setHighlightService(true);
        setTimeout(() => setHighlightService(false), 2000);
      }
    };
    window.addEventListener('preselect-service', handlePreselect);
    return () => window.removeEventListener('preselect-service', handlePreselect);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    if (formData.message.length > 500) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('service', formData.service);
      body.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6fl2maau1b90a4042p0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          {/* Left: Info */}
          <div className="space-y-6 sm:space-y-8">
            <div>
              <span className="inline-block bg-[#F4B942]/20 text-[#1A2B4A] text-sm font-semibold px-4 py-2 rounded-full mb-4 uppercase tracking-widest">
                Kontakt
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1A2B4A] leading-tight mb-4">
                Kostenlose Beratung anfragen
              </h2>
              <p className="text-gray-500 text-base sm:text-lg leading-relaxed">
                Füllen Sie das Formular aus – wir melden uns innerhalb von 24 Stunden bei Ihnen. Persönlich,
                kompetent und unverbindlich.
              </p>
            </div>

            <div className="space-y-4 sm:space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#F4B942]/15 rounded-xl shrink-0">
                  <i className="ri-phone-fill text-[#F4B942] text-xl"></i>
                </div>
                <div>
                  <p className="font-semibold text-[#1A2B4A] text-sm sm:text-base">Telefon</p>
                  <a href="tel:+436991223301" className="text-gray-500 hover:text-[#F4B942] transition-colors text-sm">
                    +43 699 122 333 01
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#F4B942]/15 rounded-xl shrink-0">
                  <i className="ri-map-pin-2-fill text-[#F4B942] text-xl"></i>
                </div>
                <div>
                  <p className="font-semibold text-[#1A2B4A] text-sm sm:text-base">Adresse</p>
                  <p className="text-gray-500 text-sm">Maroltingergasse 92/1/5, 1160 Wien</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#F4B942]/15 rounded-xl shrink-0">
                  <i className="ri-time-fill text-[#F4B942] text-xl"></i>
                </div>
                <div>
                  <p className="font-semibold text-[#1A2B4A] text-sm sm:text-base">Erreichbarkeit</p>
                  <p className="text-gray-500 text-sm">Mo–Fr: 07:00 – 17:00 Uhr</p>
                </div>
              </div>
            </div>

            <div className="bg-[#1A2B4A] rounded-2xl p-5 sm:p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <i className="ri-shield-check-fill text-[#F4B942] text-2xl"></i>
                <p className="font-bold text-base sm:text-lg">Notdienst verfügbar</p>
              </div>
              <p className="text-white/70 text-sm">
                Bei elektrischen Notfällen sind wir auch außerhalb der Geschäftszeiten für Sie erreichbar.
              </p>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-[#F8F9FB] rounded-3xl p-6 sm:p-8 shadow-lg">
            {submitStatus === 'success' ? (
              <div className="flex flex-col items-center justify-center py-12 sm:py-16 text-center space-y-4">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full">
                  <i className="ri-check-line text-green-600 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#1A2B4A]">Anfrage gesendet!</h3>
                <p className="text-gray-500 text-sm max-w-xs">
                  Vielen Dank! Wir melden uns innerhalb von 24 Stunden bei Ihnen.
                </p>
                <button
                  onClick={() => setSubmitStatus('idle')}
                  className="mt-4 bg-[#F4B942] text-[#1A2B4A] px-6 py-2 rounded-full font-semibold text-sm hover:bg-[#e5a832] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Neue Anfrage
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-4 sm:space-y-5">
                <h3 className="text-xl font-bold text-[#1A2B4A] mb-2">Ihre Anfrage</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1A2B4A] mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Max Mustermann"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#1A2B4A] placeholder-gray-400 focus:outline-none focus:border-[#F4B942] bg-white transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A2B4A] mb-1">Telefon</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+43 ..."
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#1A2B4A] placeholder-gray-400 focus:outline-none focus:border-[#F4B942] bg-white transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A2B4A] mb-1">
                    E-Mail <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="ihre@email.at"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#1A2B4A] placeholder-gray-400 focus:outline-none focus:border-[#F4B942] bg-white transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A2B4A] mb-1">Gewünschte Leistung</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className={`w-full border rounded-xl px-4 py-3 text-sm text-[#1A2B4A] focus:outline-none focus:border-[#F4B942] bg-white transition-all duration-500 cursor-pointer ${
                      highlightService ? 'border-[#F4B942] ring-2 ring-[#F4B942]/30 shadow-md' : 'border-gray-200'
                    }`}
                  >
                    <option value="">Bitte wählen...</option>
                    {services.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A2B4A] mb-1">
                    Nachricht <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="Beschreiben Sie kurz Ihr Anliegen..."
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#1A2B4A] placeholder-gray-400 focus:outline-none focus:border-[#F4B942] bg-white transition-colors resize-none"
                  />
                  <p className="text-xs text-gray-400 text-right mt-1">{formData.message.length}/500 Zeichen</p>
                </div>

                {submitStatus === 'error' && (
                  <p className="text-red-500 text-sm">Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.</p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || formData.message.length > 500}
                  className="w-full bg-[#F4B942] text-[#1A2B4A] py-4 rounded-xl font-bold text-base hover:bg-[#e5a832] transition-all duration-300 hover:shadow-lg disabled:opacity-50 cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i> Wird gesendet...
                    </span>
                  ) : (
                    'Anfrage kostenlos senden'
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  Ihre Daten werden vertraulich behandelt und nicht weitergegeben.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
